import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useApp } from "../context/AppContext";

export default function SeoCocoonView() {
  const { proj, activeId, getStore, setStore, dataRef, projects, setProjects, apiFetch,
    saveClusters, saveContent, saveStructure, saveSemClusters, saveLinks,
    flash, setView, view, dark, setDark, t, card, inp, btnP, btnG, btnSm, stitle, muted, th, td,
    userRole, currentUser, loadProjectData, openP, infoId, setInfoId, setActiveId } = useApp();
  if (!proj) return null;
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [backlinkLoading, setBacklinkLoading] = useState(false);
  const [extLinkData, setExtLinkData] = useState([]);
  const buildSceneRef = useRef(null);

  useEffect(() => {
    let destroyed = false;
    const container = containerRef.current;
    if (!container) return;

    const init = async () => {
      try {
        // Fetch graph data
        const resp = await apiFetch(`/api/projects/${activeId}/site-scan/link-graph`);
        const graphData = await resp.json();
        if (destroyed) return;
        if (!graphData.nodes || graphData.nodes.length === 0) {
          setError("Немає даних. Спочатку виконайте скан сайту.");
          setLoading(false);
          return;
        }
        setStats({ nodes: graphData.nodes.length, edges: graphData.edges.length, extNodes: (graphData.externalNodes || []).length });

        // Load extension scan data for overlay
        apiFetch(`/api/extension/link-data/${activeId}`).then(r => r.json()).then(d => { if (Array.isArray(d)) setExtLinkData(d); }).catch(() => {});

        // Create iframe with full Three.js scene (ES modules need their own document)
        const iframe = document.createElement("iframe");
        iframe.style.cssText = "width:100%;height:100%;border:none;display:block;";
        container.appendChild(iframe);

        const iDoc = iframe.contentDocument || iframe.contentWindow.document;
        iDoc.open();
        iDoc.write(buildSceneHTML(graphData, proj));
        iDoc.close();

        // Listen for messages from iframe
        const onMsg = (e) => {
          if (e.source !== iframe.contentWindow) return;
          if (e.data?.type === "node-click") {
            const nd = e.data.node;
            if (nd.external) {
              setSelectedNode({ ...nd, isExternal: true });
            } else {
              const inLinks = graphData.edges.filter(ed => ed.to === nd.url).map(ed => ({ url: ed.from, anchor: ed.anchor }));
              const outLinks = graphData.edges.filter(ed => ed.from === nd.url).map(ed => ({ url: ed.to, anchor: ed.anchor }));
              setSelectedNode({ ...nd, inLinks, outLinks });
            }
          } else if (e.data?.type === "close-detail") {
            setSelectedNode(null);
          }
        };
        window.addEventListener("message", onMsg);
        sceneRef.current = { iframe, onMsg };
        setLoading(false);
      } catch (err) {
        if (!destroyed) { setError(err.message); setLoading(false); }
      }
    };

    // Build the full HTML for the iframe scene
    const buildSceneHTML = (data, project) => {
      const nodesJSON = JSON.stringify(data.nodes);
      const edgesJSON = JSON.stringify(data.edges);
      const extNodesJSON = JSON.stringify(data.externalNodes || []);
      const extEdgesJSON = JSON.stringify(data.externalEdges || []);
      const domain = project.domain || "";
      return `<!DOCTYPE html><html><head><meta charset="UTF-8">
<link href="https://fonts.googleapis.com/css2?family=Outfit:wght@200;300;400;500;600&display=swap" rel="stylesheet">
<style>
:root{--glass-bg:rgba(255,255,255,0.03);--glass-border:rgba(255,255,255,0.08);--glass-highlight:rgba(255,255,255,0.2);--text-main:rgba(255,255,255,0.9);--text-muted:rgba(255,255,255,0.6)}
*{margin:0;padding:0;box-sizing:border-box;user-select:none}
body{overflow:hidden;background:#050508;font-family:'Outfit',sans-serif;color:var(--text-main)}
canvas{display:block;width:100%;height:100%;cursor:crosshair;position:absolute;top:0;left:0;z-index:1}
.glass-panel{backdrop-filter:blur(24px) saturate(120%);background:linear-gradient(145deg,rgba(255,255,255,0.05) 0%,rgba(255,255,255,0.01) 100%);border:1px solid var(--glass-border);border-top:1px solid var(--glass-highlight);border-left:1px solid var(--glass-highlight);box-shadow:0 20px 40px rgba(0,0,0,0.4),inset 0 0 20px rgba(255,255,255,0.02);border-radius:24px;position:absolute;z-index:10;overflow:hidden;transition:all 0.4s cubic-bezier(0.25,0.8,0.25,1)}
.glass-panel:hover{background:linear-gradient(145deg,rgba(255,255,255,0.08) 0%,rgba(255,255,255,0.02) 100%);box-shadow:0 30px 60px rgba(0,0,0,0.5),inset 0 0 20px rgba(255,255,255,0.05);transform:translateY(-2px);border-color:rgba(255,255,255,0.15)}
#info-panel{top:32px;left:32px;width:260px;padding:24px}
#info-title{font-weight:500;font-size:18px;margin-bottom:8px;letter-spacing:-0.02em;background:linear-gradient(135deg,#fff 30%,#a5b4fc 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent}
.info-text{font-size:13px;line-height:1.6;color:var(--text-muted);font-weight:300}
#hover-label{position:absolute;z-index:30;pointer-events:none;display:none;white-space:nowrap;background:rgba(10,5,25,0.75);border:1px solid rgba(180,140,255,0.18);padding:5px 10px;border-radius:8px;font-size:11px;font-weight:300;color:rgba(200,170,255,0.75);backdrop-filter:blur(8px)}
#hover-label.visible{display:block}
#detail-card{bottom:100px;left:32px;width:280px;padding:20px 22px;display:none}
#detail-card.visible{display:block}
#dc-depth{font-size:9px;font-weight:500;letter-spacing:1.5px;text-transform:uppercase;color:rgba(160,130,255,0.45);margin-bottom:8px}
#dc-title{font-size:15px;font-weight:500;color:#e8dcff;margin-bottom:6px;line-height:1.3}
#dc-url{font-size:10px;font-weight:300;color:rgba(180,140,255,0.45);word-break:break-all;margin-bottom:12px}
#dc-url a{color:rgba(180,140,255,0.55);text-decoration:none}
#dc-url a:hover{color:#c4b5fd}
#dc-stats{display:flex;gap:18px}
.dc-stat{display:flex;flex-direction:column;gap:2px}
.dc-stat-val{font-size:20px;font-weight:500;color:#d4b8ff;line-height:1}
.dc-stat-lbl{font-size:9px;font-weight:300;color:rgba(180,140,255,0.4);letter-spacing:.6px;text-transform:uppercase}
#dc-close{position:absolute;top:14px;right:16px;cursor:pointer;font-size:14px;color:rgba(180,140,255,0.3);pointer-events:all;transition:.2s}
#dc-close:hover{color:rgba(200,170,255,0.7)}
#back-btn{position:absolute;top:32px;right:32px;z-index:10;backdrop-filter:blur(24px) saturate(120%);background:linear-gradient(145deg,rgba(255,255,255,0.05),rgba(255,255,255,0.01));border:1px solid var(--glass-border);border-top:1px solid var(--glass-highlight);border-left:1px solid var(--glass-highlight);box-shadow:0 10px 20px rgba(0,0,0,0.3);border-radius:16px;padding:10px 20px;color:var(--text-muted);font-family:'Outfit',sans-serif;font-size:13px;font-weight:300;cursor:pointer;transition:all .3s}
#back-btn:hover{background:linear-gradient(145deg,rgba(255,255,255,0.1),rgba(255,255,255,0.03));color:var(--text-main);transform:translateY(-2px)}
</style></head><body>
<div id="info-panel" class="glass-panel">
<div id="info-title">${domain || "SEO Кокон"}</div>
<div class="info-text">
  Карта перелінковки<br><br>
  <span style="color:rgba(165,180,252,0.7)">${data.nodes.length}</span> сторінок · <span style="color:rgba(165,180,252,0.7)">${data.edges.length}</span> посилань<br><br>
  <span style="color:#e63636">●</span> сироти (0-2 зв'язки)<br>
  <span style="color:#f29a1a">●</span> слабкі (мало зв'язків)<br>
  <span style="color:#4dd97a">●</span> норма<br>
  <span style="color:#33b3e6">●</span> добре зв'язані<br>
  <span style="color:#804de6">●</span> хаби (багато зв'язків)<br>
  <span style="color:#fbbf24">⭐</span> зовнішні донори<br><br>
  <span style="font-size:11px;color:rgba(255,255,255,0.4)">Сторінки з малою к-стю посилань<br>виштовхуються за межі сфери.<br>Золоті точки — домени-донори.</span><br><br>
  Натисніть на вузол для деталей.<br>Тягніть для обертання.
</div>
</div>
<div id="hover-label"></div>
<div id="detail-card" class="glass-panel">
<span id="dc-close">✕</span>
<div id="dc-depth"></div>
<div id="dc-title"></div>
<div id="dc-url"></div>
<div id="dc-stats">
  <div class="dc-stat"><div class="dc-stat-val" id="dc-in">0</div><div class="dc-stat-lbl">вхідних</div></div>
  <div class="dc-stat"><div class="dc-stat-val" id="dc-out">0</div><div class="dc-stat-lbl">вихідних</div></div>
  <div class="dc-stat"><div class="dc-stat-val" id="dc-depth2">0</div><div class="dc-stat-lbl">рівень</div></div>
</div>
</div>
<button id="back-btn" onclick="window.parent.postMessage({type:'back'},'*')">← Дашборд</button>
<canvas id="c"></canvas>
<script type="importmap">
{"imports":{"three":"https://cdn.jsdelivr.net/npm/three@0.162.0/build/three.module.js","three/addons/":"https://cdn.jsdelivr.net/npm/three@0.162.0/examples/jsm/"}}
</script>
<script type="module">
import*as THREE from'three';
import{OrbitControls}from'three/addons/controls/OrbitControls.js';
import{EffectComposer}from'three/addons/postprocessing/EffectComposer.js';
import{RenderPass}from'three/addons/postprocessing/RenderPass.js';
import{UnrealBloomPass}from'three/addons/postprocessing/UnrealBloomPass.js';
import{OutputPass}from'three/addons/postprocessing/OutputPass.js';

const nodes=${nodesJSON};
const edges=${edgesJSON};
const extNodes=${extNodesJSON};
const extEdges=${extEdgesJSON};

// Build node/edge data
const nodeData=[];
const urlIdx=new Map();

// URL depth helper
const getDepth=(url)=>{try{const p=new URL(url).pathname.replace(/\\/+$/,'');return p?p.split('/').filter(Boolean).length:0}catch{return 0}};

// Index nodes and build edges first
nodes.forEach((n,i)=>{urlIdx.set(n.url,i)});
const allEdges=[];
edges.forEach(e=>{const fi=urlIdx.get(e.from),ti=urlIdx.get(e.to);if(fi!==undefined&&ti!==undefined)allEdges.push([fi,ti,0.9])});

// Deduplicate edges
const edgeSet=new Set();
const dedupEdges=[];
allEdges.forEach(([a,b,w])=>{const k=Math.min(a,b)+','+Math.max(a,b);if(!edgeSet.has(k)){edgeSet.add(k);dedupEdges.push([a,b,w])}});

// Limit to 5000 edges max (keep most connected)
const MAX_EDGES=5000;
let edgePairs=dedupEdges;
if(dedupEdges.length>MAX_EDGES){
const tmpConn=new Array(nodes.length).fill(0);
dedupEdges.forEach(([a,b])=>{tmpConn[a]++;tmpConn[b]++});
dedupEdges.sort((a,b)=>(tmpConn[b[0]]+tmpConn[b[1]])-(tmpConn[a[0]]+tmpConn[a[1]]));
edgePairs=dedupEdges.slice(0,MAX_EDGES);
}

// Count connections per node
const connCount=new Array(nodes.length).fill(0);
edgePairs.forEach(([a,b])=>{connCount[a]++;connCount[b]++});
let maxConn=1;for(let i=0;i<connCount.length;i++){if(connCount[i]>maxConn)maxConn=connCount[i]}

// --- SPHERE layout: center nucleus + all nodes on sphere surface ---
const N=nodes.length;
const ga=Math.PI*(3-Math.sqrt(5)); // golden angle for Fibonacci sphere
const positions=new Array(N);

// Find main page (depth 0 or homepage /)
const mainIdx=[];
const orbitIdx=[];
nodes.forEach((n,i)=>{
  const d0=getDepth(n.url);
  if(d0===0) mainIdx.push(i);
  else orbitIdx.push(i);
});

// Main page at exact center (0,0,0)
mainIdx.forEach(idx=>{positions[idx]=new THREE.Vector3(0,0,0)});

// Sphere radius
const R=28;

// Full Fibonacci sphere — even distribution across ENTIRE sphere
orbitIdx.forEach((idx,rank)=>{
  const total=orbitIdx.length;
  // y goes from +1 to -1 (full sphere, not half)
  const phi=Math.acos(1-2*(rank+0.5)/total);
  const theta=ga*rank;
  positions[idx]=new THREE.Vector3(
    R*Math.sin(phi)*Math.cos(theta),
    R*Math.cos(phi),
    R*Math.sin(phi)*Math.sin(theta)
  );
});

// Build nodeData
nodes.forEach((n,i)=>{
const d=getDepth(n.url);
const totalLinks=n.linksIn+n.linksOut;
const sz=d===0?4.0:totalLinks===0?0.7:Math.max(0.6,Math.min(2.5,0.6+totalLinks*0.08));
nodeData.push({label:n.title||n.url,url:n.url,depth:d,pos:positions[i],size:sz,distFromRoot:positions[i].length(),linksIn:n.linksIn,linksOut:n.linksOut,conn:connCount[i]});
});

// Color by connectivity: red(orphan) → yellow(few) → cyan(ok) → blue/purple(hub)
function connColor(count,max){
const t=Math.pow(count/Math.max(max,1),0.4);
if(t<0.15)return new THREE.Color(0.9,0.15,0.15);  // red — orphans
if(t<0.35)return new THREE.Color(0.95,0.6,0.1);    // orange — weak
if(t<0.55)return new THREE.Color(0.3,0.85,0.5);    // green — ok
if(t<0.75)return new THREE.Color(0.2,0.7,0.9);     // cyan — good
return new THREE.Color(0.5,0.3,0.9);                // purple — hub
}

// Scene
const scene=new THREE.Scene();
scene.fog=new THREE.FogExp2(0x000000,0.0025);
const camera=new THREE.PerspectiveCamera(65,innerWidth/innerHeight,0.1,1000);
camera.position.set(0,15,55);
const canvas=document.getElementById('c');
const renderer=new THREE.WebGLRenderer({canvas,antialias:true,powerPreference:'high-performance'});
renderer.setSize(innerWidth,innerHeight);
renderer.setPixelRatio(Math.min(devicePixelRatio,2));
renderer.setClearColor(0x000000);
renderer.outputColorSpace=THREE.SRGBColorSpace;

// Stars
{const N=8000,pos=[],col=[],sz=[];for(let i=0;i<N;i++){const r=THREE.MathUtils.randFloat(60,180);const ph=Math.acos(THREE.MathUtils.randFloatSpread(2));const th=Math.random()*Math.PI*2;pos.push(r*Math.sin(ph)*Math.cos(th),r*Math.sin(ph)*Math.sin(th),r*Math.cos(ph));const c=Math.random();if(c<0.7)col.push(1,1,1);else if(c<0.85)col.push(0.7,0.8,1);else col.push(1,0.9,0.8);sz.push(THREE.MathUtils.randFloat(0.05,0.25))}
const g=new THREE.BufferGeometry();g.setAttribute('position',new THREE.Float32BufferAttribute(pos,3));g.setAttribute('color',new THREE.Float32BufferAttribute(col,3));g.setAttribute('size',new THREE.Float32BufferAttribute(sz,1));
const m=new THREE.ShaderMaterial({vertexShader:\`attribute float size;varying vec3 vColor;void main(){vColor=color;vec4 mv=modelViewMatrix*vec4(position,1.);gl_PointSize=size*(300./-mv.z);gl_Position=projectionMatrix*mv;}\`,fragmentShader:\`varying vec3 vColor;void main(){vec2 c=gl_PointCoord-.5;float d=length(c);if(d>.5)discard;gl_FragColor=vec4(vColor,(1.-smoothstep(0.,.5,d))*.8);}\`,transparent:true,depthWrite:false,blending:THREE.AdditiveBlending,vertexColors:true});
const stars=new THREE.Points(g,m);scene.add(stars);scene.userData.stars=stars}

const controls=new OrbitControls(camera,canvas);
controls.enableDamping=true;controls.dampingFactor=0.05;controls.rotateSpeed=0.6;controls.minDistance=8;controls.maxDistance=120;controls.autoRotate=true;controls.autoRotateSpeed=0.15;controls.enablePan=false;

const composer=new EffectComposer(renderer);
composer.addPass(new RenderPass(scene,camera));
if(edgePairs.length<2000){const bloom=new UnrealBloomPass(new THREE.Vector2(innerWidth,innerHeight),0.8,0.4,0.85);composer.addPass(bloom)}
composer.addPass(new OutputPass());

// Node shader
const nodeVS=\`attribute float nodeSize;attribute float nodeType;attribute vec3 nodeColor;attribute float distanceFromRoot;uniform float uBaseNodeSize;varying vec3 vColor;varying float vNodeType;varying vec3 vPosition;varying float vDistanceFromRoot;void main(){vNodeType=nodeType;vColor=nodeColor;vDistanceFromRoot=distanceFromRoot;vec3 wp=(modelMatrix*vec4(position,1.)).xyz;vPosition=wp;vec4 mv=modelViewMatrix*vec4(position,1.);gl_PointSize=nodeSize*uBaseNodeSize*(1000./-mv.z);gl_Position=projectionMatrix*mv;}\`;
const nodeFS=\`varying vec3 vColor;varying float vNodeType;varying vec3 vPosition;varying float vDistanceFromRoot;void main(){vec2 c=2.*gl_PointCoord-1.;float d=length(c);if(d>1.)discard;float g1=1.-smoothstep(0.,.5,d);float g2=1.-smoothstep(0.,1.,d);float gs=pow(g1,1.2)+g2*.3;vec3 fc=vColor;float cb=smoothstep(.4,0.,d);fc+=vec3(1.)*cb*.3;float alpha=gs*(.95-.3*d);float df=smoothstep(100.,15.,length(vPosition-cameraPosition));if(vNodeType>.5){fc*=1.1;alpha*=.9;}gl_FragColor=vec4(fc,alpha*df);}\`;

// Connections use simple LineBasicMaterial

// Build meshes
const nPos=[],nType=[],nSz=[],nCol=[],nDist=[];
nodeData.forEach(n=>{nPos.push(n.pos.x,n.pos.y,n.pos.z);nType.push(n.depth===0?2:n.conn<3?1:0);nSz.push(n.size);let c;if(n.depth===0){c=new THREE.Color(1,1,1)}else{c=connColor(n.conn,maxConn);c.offsetHSL(THREE.MathUtils.randFloatSpread(.02),0,THREE.MathUtils.randFloatSpread(.05))}nCol.push(c.r,c.g,c.b);nDist.push(n.distFromRoot)});
const gn=new THREE.BufferGeometry();
gn.setAttribute('position',new THREE.Float32BufferAttribute(nPos,3));
gn.setAttribute('nodeType',new THREE.Float32BufferAttribute(nType,1));
gn.setAttribute('nodeSize',new THREE.Float32BufferAttribute(nSz,1));
gn.setAttribute('nodeColor',new THREE.Float32BufferAttribute(nCol,3));
gn.setAttribute('distanceFromRoot',new THREE.Float32BufferAttribute(nDist,1));
const mn=new THREE.ShaderMaterial({uniforms:{uBaseNodeSize:{value:0.9}},vertexShader:nodeVS,fragmentShader:nodeFS,transparent:true,depthWrite:false,blending:THREE.AdditiveBlending});
const nodesMesh=new THREE.Points(gn,mn);scene.add(nodesMesh);

// Connections — violet lines with additive glow
const edgeOpacity=Math.max(0.03,Math.min(0.35,6/Math.sqrt(edgePairs.length||1)));
const lineMat=new THREE.LineBasicMaterial({color:0xb4befe,transparent:true,opacity:edgeOpacity,blending:THREE.AdditiveBlending,depthWrite:false});
const seen=new Set();const linePos=[];
edgePairs.forEach(([ai,bi])=>{const key=Math.min(ai,bi)+','+Math.max(ai,bi);if(seen.has(key))return;seen.add(key);const na=nodeData[ai],nb=nodeData[bi];if(!na||!nb)return;linePos.push(na.pos.x,na.pos.y,na.pos.z,nb.pos.x,nb.pos.y,nb.pos.z)});
if(linePos.length>0){const lg=new THREE.BufferGeometry();lg.setAttribute('position',new THREE.Float32BufferAttribute(linePos,3));scene.add(new THREE.LineSegments(lg,lineMat))}

// ─── External donor nodes (gold, outside sphere) ───
const extNodeData=[];
const extDomainIdx=new Map();
let extNodesMesh=null;
if(extNodes.length>0){
const ga2=Math.PI*(3-Math.sqrt(5));
const extPos=[],extSz=[],extCol=[],extDist=[],extType=[];
extNodes.forEach((en,i)=>{
  extDomainIdx.set(en.domain,i);
  const r=55+Math.random()*12;
  const y=1-(i/Math.max(extNodes.length-1,1))*2;
  const rad=Math.sqrt(1-y*y)*r;
  const theta=ga2*i;
  const pos=new THREE.Vector3(rad*Math.cos(theta)+(Math.random()-.5)*0.5,y*r+(Math.random()-.5)*0.5,rad*Math.sin(theta)+(Math.random()-.5)*0.5);
  const sz=Math.max(0.6,Math.min(2.5,0.6+en.backlinksCount*0.15));
  extNodeData.push({domain:en.domain,pos,size:sz,backlinksCount:en.backlinksCount,avgRank:en.avgRank,distFromRoot:pos.length()});
  extPos.push(pos.x,pos.y,pos.z);extType.push(2);extSz.push(sz);extCol.push(1.0,0.85,0.2);extDist.push(pos.length());
});
const gExt=new THREE.BufferGeometry();
gExt.setAttribute('position',new THREE.Float32BufferAttribute(extPos,3));
gExt.setAttribute('nodeType',new THREE.Float32BufferAttribute(extType,1));
gExt.setAttribute('nodeSize',new THREE.Float32BufferAttribute(extSz,1));
gExt.setAttribute('nodeColor',new THREE.Float32BufferAttribute(extCol,3));
gExt.setAttribute('distanceFromRoot',new THREE.Float32BufferAttribute(extDist,1));
const extMat=new THREE.ShaderMaterial({uniforms:{uBaseNodeSize:{value:0.6}},vertexShader:nodeVS,fragmentShader:nodeFS,transparent:true,depthWrite:false,blending:THREE.AdditiveBlending});
extNodesMesh=new THREE.Points(gExt,extMat);scene.add(extNodesMesh);

// External edges — gold/amber lines from donor to internal pages
const extLinePos=[];
extEdges.forEach(ee=>{
  const di=extDomainIdx.get(ee.fromDomain);
  const ti=urlIdx.get(ee.to);
  if(di===undefined||ti===undefined)return;
  const from=extNodeData[di].pos;const to=nodeData[ti].pos;
  extLinePos.push(from.x,from.y,from.z,to.x,to.y,to.z);
});
if(extLinePos.length>0){
  const extLineGeo=new THREE.BufferGeometry();
  extLineGeo.setAttribute('position',new THREE.Float32BufferAttribute(extLinePos,3));
  const extLineMat=new THREE.LineBasicMaterial({color:0xfbbf24,transparent:true,opacity:Math.max(0.04,Math.min(0.25,4/Math.sqrt(extLinePos.length/6||1))),blending:THREE.AdditiveBlending,depthWrite:false});
  scene.add(new THREE.LineSegments(extLineGeo,extLineMat));
}
}

// Interaction
const raycaster=new THREE.Raycaster();raycaster.params.Points.threshold=0.5;
const pointer=new THREE.Vector2(-9,-9);
let hoveredIdx=-1;
const hoverLabel=document.getElementById('hover-label');
const detailCard=document.getElementById('detail-card');
const DEPTH_LABELS=['Головна','Розділ','Підкатегорія','Сторінка','Глибока'];

// Connection counts (already computed above)

document.getElementById('dc-close').addEventListener('click',e=>{e.stopPropagation();detailCard.classList.remove('visible');window.parent.postMessage({type:'close-detail'},'*')});

let hoveredExt=-1;

function showDetail(idx){
const n=nodeData[idx];
let path;try{path=new URL(n.url).pathname||'/'}catch{path=n.url}
document.getElementById('dc-depth').textContent=DEPTH_LABELS[Math.min(n.depth,4)]||'Вузол';
document.getElementById('dc-title').textContent=n.label;
document.getElementById('dc-url').innerHTML='<a href="'+n.url+'" target="_blank">'+path+'</a>';
document.getElementById('dc-in').textContent=n.linksIn;
document.getElementById('dc-out').textContent=n.linksOut;
document.getElementById('dc-depth2').textContent=n.depth;
detailCard.classList.add('visible');
window.parent.postMessage({type:'node-click',node:{url:n.url,title:n.label,linksIn:n.linksIn,linksOut:n.linksOut,depth:n.depth}},'*');
}

function showExtDetail(idx){
const n=extNodeData[idx];
detailCard.classList.remove('visible');
const targets=extEdges.filter(e=>e.fromDomain===n.domain).map(e=>({url:e.to,anchor:e.anchor,count:e.count}));
window.parent.postMessage({type:'node-click',node:{domain:n.domain,backlinksCount:n.backlinksCount,avgRank:n.avgRank,targets,external:true}},'*');
}

canvas.addEventListener('click',e=>{if(hoveredIdx>=0){showDetail(hoveredIdx);controls.autoRotate=false}else if(hoveredExt>=0){showExtDetail(hoveredExt);controls.autoRotate=false}else{detailCard.classList.remove('visible');controls.autoRotate=true;window.parent.postMessage({type:'close-detail'},'*')}});

canvas.addEventListener('mousemove',e=>{pointer.x=(e.clientX/innerWidth)*2-1;pointer.y=-(e.clientY/innerHeight)*2+1;raycaster.setFromCamera(pointer,camera);
// Check internal nodes
let idx=-1;if(nodesMesh){const hits=raycaster.intersectObject(nodesMesh);idx=hits.length>0?hits[0].index:-1}
// Check external nodes
let extIdx=-1;if(extNodesMesh){const extHits=raycaster.intersectObject(extNodesMesh);extIdx=extHits.length>0?extHits[0].index:-1}
if(idx!==hoveredIdx||extIdx!==hoveredExt){hoveredIdx=idx;hoveredExt=extIdx;
  if(idx>=0){const n=nodeData[idx];hoverLabel.textContent=n.label;hoverLabel.style.left=(e.clientX+14)+'px';hoverLabel.style.top=(e.clientY-28)+'px';hoverLabel.classList.add('visible');canvas.style.cursor='pointer'}
  else if(extIdx>=0){const n=extNodeData[extIdx];hoverLabel.textContent=n.domain+' ('+n.backlinksCount+' посилань, DR '+n.avgRank+')';hoverLabel.style.left=(e.clientX+14)+'px';hoverLabel.style.top=(e.clientY-28)+'px';hoverLabel.classList.add('visible');canvas.style.cursor='pointer'}
  else{hoverLabel.classList.remove('visible');canvas.style.cursor='crosshair'}
}else if(idx>=0||extIdx>=0){hoverLabel.style.left=(e.clientX+14)+'px';hoverLabel.style.top=(e.clientY-28)+'px'}
});

function animate(){requestAnimationFrame(animate);scene.userData.stars.rotation.y+=.0002;controls.update();composer.render()}
window.addEventListener('resize',()=>{camera.aspect=innerWidth/innerHeight;camera.updateProjectionMatrix();renderer.setSize(innerWidth,innerHeight);composer.setSize(innerWidth,innerHeight)});
animate();
<\/script></body></html>`;
    };

    buildSceneRef.current = buildSceneHTML;

    init();

    // Listen for back button from iframe
    const onBackMsg = (e) => {
      if (e.data?.type === "back") setView("dashboard");
      if (e.data?.type === "close-detail") setSelectedNode(null);
    };
    window.addEventListener("message", onBackMsg);

    return () => {
      destroyed = true;
      window.removeEventListener("message", onBackMsg);
      if (sceneRef.current) {
        window.removeEventListener("message", sceneRef.current.onMsg);
        if (sceneRef.current.iframe && sceneRef.current.iframe.parentNode) {
          sceneRef.current.iframe.parentNode.removeChild(sceneRef.current.iframe);
        }
      }
    };
  }, [activeId]);

  const loadBacklinks = async () => {
    setBacklinkLoading(true);
    try {
      const resp = await apiFetch(`/api/projects/${activeId}/serpstat/backlinks`);
      const data = await resp.json();
      if (data.error) { flash(data.error); setBacklinkLoading(false); return; }
      flash(`Завантажено ${data.count} бэклінків${data.cached ? " (кеш)" : ""}`);
      // Remove old iframe
      if (sceneRef.current) {
        window.removeEventListener("message", sceneRef.current.onMsg);
        if (sceneRef.current.iframe?.parentNode) {
          sceneRef.current.iframe.parentNode.removeChild(sceneRef.current.iframe);
        }
      }
      // Re-fetch link-graph which now includes external data
      const resp2 = await apiFetch(`/api/projects/${activeId}/site-scan/link-graph`);
      const graphData = await resp2.json();
      setStats({ nodes: graphData.nodes.length, edges: graphData.edges.length, extNodes: (graphData.externalNodes || []).length });
      const container = containerRef.current;
      if (!container || !buildSceneRef.current) return;
      const iframe = document.createElement("iframe");
      iframe.style.cssText = "width:100%;height:100%;border:none;display:block;";
      container.appendChild(iframe);
      const iDoc = iframe.contentDocument || iframe.contentWindow.document;
      iDoc.open();
      iDoc.write(buildSceneRef.current(graphData, proj));
      iDoc.close();
      const onMsg = (e) => {
        if (e.source !== iframe.contentWindow) return;
        if (e.data?.type === "node-click") {
          const nd = e.data.node;
          if (nd.external) {
            setSelectedNode({ ...nd, isExternal: true });
          } else {
            const inLinks = graphData.edges.filter(ed => ed.to === nd.url).map(ed => ({ url: ed.from, anchor: ed.anchor }));
            const outLinks = graphData.edges.filter(ed => ed.from === nd.url).map(ed => ({ url: ed.to, anchor: ed.anchor }));
            setSelectedNode({ ...nd, inLinks, outLinks });
          }
        } else if (e.data?.type === "close-detail") {
          setSelectedNode(null);
        }
      };
      window.addEventListener("message", onMsg);
      sceneRef.current = { iframe, onMsg };
    } catch (err) { flash("Помилка: " + err.message); }
    setBacklinkLoading(false);
  };

  return (
    <div ref={containerRef} data-cocoon-container style={{ position: "relative", width: "100%", height: "calc(100vh - 60px)", background: "#050508", borderRadius: 12, overflow: "hidden" }}>
      {loading && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#a5b4fc", fontSize: 16, background: "#050508", zIndex: 20, fontFamily: "Outfit, sans-serif" }}>
          Завантаження графу перелінковки...
        </div>
      )}
      {error && (
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#f87171", fontSize: 14, background: "#050508", gap: 12, zIndex: 20 }}>
          <div>{error}</div>
        </div>
      )}
      {!loading && !error && (
        <div style={{ position: "absolute", bottom: 32, right: 32, zIndex: 20, display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
          <button
            onClick={loadBacklinks}
            disabled={backlinkLoading}
            style={{
              backdropFilter: "blur(24px) saturate(120%)",
              background: "linear-gradient(145deg, rgba(251,191,36,0.15), rgba(251,191,36,0.05))",
              border: "1px solid rgba(251,191,36,0.3)",
              borderRadius: 16, padding: "10px 20px",
              color: "#fbbf24", fontFamily: "Outfit, sans-serif", fontSize: 13, fontWeight: 400,
              cursor: backlinkLoading ? "wait" : "pointer",
              opacity: backlinkLoading ? 0.6 : 1,
              transition: "all .3s",
              boxShadow: "0 10px 20px rgba(0,0,0,0.3)",
            }}
          >
            {backlinkLoading ? "Завантаження..." : `⭐ Завантажити бэклінки${stats?.extNodes ? ` (${stats.extNodes})` : ""}`}
          </button>
        </div>
      )}
      {selectedNode && !selectedNode.isExternal && (() => {
        const ext = extLinkData.find(e => { try { return new URL(e.url).pathname === new URL(selectedNode.url).pathname; } catch { return false; } });
        return (
          <div style={{
            position: "absolute", top: 32, left: 32, zIndex: 20, width: 320, padding: "20px 22px",
            backdropFilter: "blur(24px) saturate(120%)",
            background: "linear-gradient(145deg, rgba(165,180,252,0.08), rgba(165,180,252,0.02))",
            border: "1px solid rgba(165,180,252,0.2)", borderRadius: 24,
            boxShadow: "0 20px 40px rgba(0,0,0,0.4)", fontFamily: "Outfit, sans-serif",
          }}>
            <div style={{ fontSize: 9, fontWeight: 500, letterSpacing: 1.5, textTransform: "uppercase", color: "rgba(165,180,252,0.5)", marginBottom: 8 }}>
              {selectedNode.depth === 0 ? "ГОЛОВНА" : selectedNode.depth === 1 ? "РОЗДІЛ" : selectedNode.depth === 2 ? "ПІДКАТЕГОРІЯ" : "СТОРІНКА"}
            </div>
            <div style={{ fontSize: 14, fontWeight: 500, color: "#a5b4fc", marginBottom: 4, lineHeight: 1.4 }}>{selectedNode.title || selectedNode.url}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 12 }}>{(() => { try { return new URL(selectedNode.url).pathname; } catch { return selectedNode.url; } })()}</div>
            <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
              <div><div style={{ fontSize: 18, fontWeight: 500, color: "#a5b4fc" }}>{selectedNode.inLinks?.length || 0}</div><div style={{ fontSize: 9, color: "rgba(165,180,252,0.5)", textTransform: "uppercase" }}>вхідних</div></div>
              <div><div style={{ fontSize: 18, fontWeight: 500, color: "#a5b4fc" }}>{selectedNode.outLinks?.length || 0}</div><div style={{ fontSize: 9, color: "rgba(165,180,252,0.5)", textTransform: "uppercase" }}>вихідних</div></div>
              <div><div style={{ fontSize: 18, fontWeight: 500, color: "#a5b4fc" }}>{selectedNode.depth}</div><div style={{ fontSize: 9, color: "rgba(165,180,252,0.5)", textTransform: "uppercase" }}>рівень</div></div>
            </div>
            {ext && (
              <div style={{ borderTop: "1px solid rgba(165,180,252,0.1)", paddingTop: 10, marginTop: 4 }}>
                <div style={{ fontSize: 9, textTransform: "uppercase", color: "rgba(165,180,252,0.4)", marginBottom: 6, letterSpacing: 1 }}>Extension дані</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px", fontSize: 11 }}>
                  <div style={{ color: "rgba(255,255,255,0.4)" }}>Слова:</div><div style={{ color: "#e2e8f0" }}>{ext.word_count || "—"}</div>
                  <div style={{ color: "rgba(255,255,255,0.4)" }}>Вн. посилання:</div><div style={{ color: "#e2e8f0" }}>{ext.internal_links ?? "—"}</div>
                  <div style={{ color: "rgba(255,255,255,0.4)" }}>Зовн. посилання:</div><div style={{ color: "#e2e8f0" }}>{ext.external_links ?? "—"}</div>
                  {ext.cms && <><div style={{ color: "rgba(255,255,255,0.4)" }}>CMS:</div><div style={{ color: "#e2e8f0" }}>{ext.cms}</div></>}
                  {ext.cloudflare && <><div style={{ color: "rgba(255,255,255,0.4)" }}>Cloudflare:</div><div style={{ color: "#fbbf24" }}>Так</div></>}
                </div>
                {ext.headings && JSON.parse(ext.headings).length > 0 && (
                  <div style={{ marginTop: 8, maxHeight: 100, overflowY: "auto" }}>
                    <div style={{ fontSize: 9, textTransform: "uppercase", color: "rgba(165,180,252,0.4)", marginBottom: 4 }}>Заголовки</div>
                    {JSON.parse(ext.headings).slice(0, 10).map((h, i) => (
                      <div key={i} style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", paddingLeft: h.tag === "h1" ? 0 : h.tag === "h2" ? 8 : 16, lineHeight: 1.5 }}>
                        <span style={{ color: "#a5b4fc", fontWeight: 600 }}>{h.tag.toUpperCase()}</span> {h.text?.slice(0, 50)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div onClick={() => setSelectedNode(null)} style={{ position: "absolute", top: 14, right: 16, cursor: "pointer", fontSize: 14, color: "rgba(165,180,252,0.3)" }}>✕</div>
          </div>
        );
      })()}
      {selectedNode?.isExternal && (
        <div style={{
          position: "absolute", bottom: 32, left: 32, zIndex: 20, width: 300, padding: "20px 22px",
          backdropFilter: "blur(24px) saturate(120%)",
          background: "linear-gradient(145deg, rgba(251,191,36,0.08), rgba(251,191,36,0.02))",
          border: "1px solid rgba(251,191,36,0.2)", borderRadius: 24,
          boxShadow: "0 20px 40px rgba(0,0,0,0.4)", fontFamily: "Outfit, sans-serif",
        }}>
          <div style={{ fontSize: 9, fontWeight: 500, letterSpacing: 1.5, textTransform: "uppercase", color: "rgba(251,191,36,0.5)", marginBottom: 8 }}>ЗОВНІШНІЙ ДОНОР</div>
          <div style={{ fontSize: 15, fontWeight: 500, color: "#fbbf24", marginBottom: 6 }}>{selectedNode.domain}</div>
          <div style={{ display: "flex", gap: 18, marginBottom: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div style={{ fontSize: 20, fontWeight: 500, color: "#fbbf24" }}>{selectedNode.backlinksCount}</div>
              <div style={{ fontSize: 9, fontWeight: 300, color: "rgba(251,191,36,0.5)", letterSpacing: 0.6, textTransform: "uppercase" }}>посилань</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div style={{ fontSize: 20, fontWeight: 500, color: "#fbbf24" }}>{selectedNode.avgRank}</div>
              <div style={{ fontSize: 9, fontWeight: 300, color: "rgba(251,191,36,0.5)", letterSpacing: 0.6, textTransform: "uppercase" }}>DR</div>
            </div>
          </div>
          {selectedNode.targets?.length > 0 && (
            <div style={{ maxHeight: 150, overflowY: "auto", fontSize: 11, color: "rgba(255,255,255,0.6)" }}>
              <div style={{ fontSize: 9, textTransform: "uppercase", color: "rgba(251,191,36,0.4)", marginBottom: 4 }}>Посилається на:</div>
              {selectedNode.targets.map((t, i) => {
                let path; try { path = new URL(t.url).pathname || "/"; } catch { path = t.url; }
                return <div key={i} style={{ marginBottom: 3, lineHeight: 1.4 }}>{path}{t.anchor ? ` — "${t.anchor}"` : ""}</div>;
              })}
            </div>
          )}
          <div onClick={() => setSelectedNode(null)} style={{ position: "absolute", top: 14, right: 16, cursor: "pointer", fontSize: 14, color: "rgba(251,191,36,0.3)" }}>✕</div>
        </div>
      )}
    </div>
  );
};

// ═══ POSITION TRACKER VIEW ═══
